import { useEffect, useRef, useState, type FormEvent } from 'react';

import { api } from './api';
import { CloseIcon, DebtIcon } from './icons';

export interface InboundDebtInvoice {
  id: string;
  sku: string;
  partnerName: string;
  remaining: number;
}

function money(value: number): string {
  return new Intl.NumberFormat('vi-VN').format(Number(value) || 0);
}

function numeric(value: string): number {
  return Number(value.replace(/[^0-9]/g, '')) || 0;
}

export function InboundPaymentModal({
  invoice,
  onClose,
  onPaid
}: {
  invoice: InboundDebtInvoice;
  onClose: () => void;
  onPaid: (message: string) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [paid, setPaid] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => input.current?.focus(), []);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onClose();
    };
    document.addEventListener('keydown', close);
    return () => document.removeEventListener('keydown', close);
  }, [onClose, saving]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (paid <= 0) {
      setError('Số tiền trả nợ phải lớn hơn 0.');
      input.current?.focus();
      return;
    }
    if (paid > invoice.remaining) {
      setError('Số tiền trả nợ không được lớn hơn số tiền còn lại.');
      input.current?.focus();
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api(`/api/invoices/inbound/${invoice.id}/payments`, {
        method: 'POST',
        body: JSON.stringify({ paid })
      });
      onPaid(`Đã trả ${money(paid)} ₫ cho hóa đơn ${invoice.sku}.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Không thể ghi nhận trả nợ.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="drawer-backdrop payment-backdrop" role="presentation">
      <section className="payment-modal" role="dialog" aria-modal="true" aria-labelledby="payment-title">
        <header className="drawer-header"><h2 id="payment-title">Trả nợ hóa đơn nhập</h2><button type="button" className="icon-button" onClick={onClose} aria-label="Đóng"><CloseIcon /></button></header>
        <form onSubmit={submit}>
          <div className="payment-content">
            <div className="payment-invoice">
              <div><span>Mã hóa đơn</span><strong>{invoice.sku}</strong></div>
              <div><span>Nhà phân phối</span><strong title={invoice.partnerName}>{invoice.partnerName}</strong></div>
              <div className="payment-remaining"><span>Còn phải trả</span><b>{money(invoice.remaining)} ₫</b></div>
            </div>
            <div className="field payment-field">
              <label htmlFor="debt-paid">Số tiền trả</label>
              <div className="payment-field-control">
                <input ref={input} id="debt-paid" inputMode="numeric" value={money(paid)} aria-invalid={Boolean(error)} onChange={(event) => { setPaid(numeric(event.target.value)); setError(''); }} />
                <button type="button" title="Điền toàn bộ số tiền còn lại" onClick={() => { setPaid(invoice.remaining); setError(''); }}>Trả toàn bộ</button>
              </div>
              {error ? <span className="field-error" role="alert">{error}</span> : null}
            </div>
          </div>
          <footer className="drawer-footer"><button type="button" className="secondary-action" onClick={onClose}>Hủy</button><button className="primary-action" disabled={saving}>{saving ? <span className="button-spinner" /> : <DebtIcon />} {saving ? 'Đang lưu…' : 'Xác nhận trả nợ'}</button></footer>
        </form>
      </section>
    </div>
  );
}
